import { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import JsonLd from '@/components/base/JsonLd';
import { allArticles } from '@/mocks/articles';
import { SITE_URL } from '@/constants/env';

function getArticleBySlug(slug: string) {
  const cleanSlug = slug.startsWith('/') ? slug : `/${slug}`;
  return allArticles.find((a) => a.slug === cleanSlug) || allArticles[0];
}

interface ArticleContent {
  metaTitle: string;
  metaDescription: string;
  sections: { heading: string; paragraphs: string[] }[];
  table?: { headers: string[]; rows: string[][] };
  faqs: { question: string; answer: string }[];
}

function getArticleContent(slug: string): ArticleContent {
  const cleanSlug = slug.startsWith('/') ? slug : `/${slug}`;

  const contentMap: Record<string, ArticleContent> = {
    '/what-is-oopbuy-spreadsheet': {
      metaTitle: "What is OOPBuy Spreadsheet? Complete Beginner's Guide 2026",
      metaDescription: 'Discover what OOPBuy Spreadsheet is and how it transforms product sourcing. Step-by-step guide covering features, benefits, and how to get started with OOPBuy Spreadsheet today.',
      sections: [
        {
          heading: 'What Exactly Is OOPBuy Spreadsheet?',
          paragraphs: [
            'At its core, the <strong>OOPBuy Spreadsheet</strong> is a structured, data-rich product catalog that organizes thousands of fashion and lifestyle items into a single, navigable interface. Rather than browsing through endless product pages or juggling multiple browser tabs, the spreadsheet consolidates product listings, category links, pricing information, and supplier references into one accessible document.',
            'Think of it as a command center for your shopping operations. Whether you are an individual buyer looking for the best deals, a reseller curating inventory, or a sourcing agent managing bulk orders, the OOPBuy Spreadsheet gives you a birds-eye view of what is available across 14 curated categories — from Shoes and Hoodies to Accessories and Perfumes.',
            'The spreadsheet is regularly updated with fresh listings, ensuring that the product data you are working with reflects current availability and pricing. Unlike static catalogs that become outdated within weeks, the OOPBuy Spreadsheet maintains a dynamic connection to the main Kaspreadsheet platform, so you are always looking at live information.',
          ],
        },
        {
          heading: 'How OOPBuy Spreadsheet Works: Step-by-Step',
          paragraphs: [
            'Getting started with the OOPBuy Spreadsheet is straightforward. First, navigate to the Kaspreadsheet website using the quick-access category links provided throughout this guide. Each category — Shoes, Jackets, Bags, and more — has a direct link that takes you straight to the relevant product listings.',
            'Once inside a category, use the spreadsheets filtering and sorting features to compare items side by side. Look at pricing, availability, and product details all within the same view — no tab-switching required. After identifying the products you want, proceed through the Kaspreadsheet checkout process.',
            'The beauty of this system lies in its simplicity. Instead of hunting for products across scattered sources, the OOPBuy Spreadsheet centralizes everything into one organized interface.',
          ],
        },
        {
          heading: 'Key Benefits of Using OOPBuy Spreadsheet',
          paragraphs: [
            'One standout benefit is <strong>centralized access</strong>. Having all 14 product categories in one place saves buyers hours of manual browsing across multiple sources. The spreadsheet is also consistently updated, meaning product information stays fresh and reliable.',
            'Another major advantage is <strong>beginner-friendliness</strong>. The OOPBuy Spreadsheet does not require technical expertise to operate. The category dropdown and visual grid make navigation intuitive, and the product comparison tools help even first-time users make informed purchasing decisions.',
            'Finally, the <strong>cost factor</strong> sets OOPBuy Spreadsheet apart from many alternatives. It offers free access to its core features, making it an attractive option for buyers who want professional-grade sourcing tools without subscription fees.',
          ],
        },
      ],
      table: {
        headers: ['Feature', 'OOPBuy Spreadsheet', 'Manual Browsing', 'Other Aggregators'],
        rows: [
          ['Ease of Use', 'High', 'Medium', 'Low'],
          ['Automation', 'Full', 'None', 'Partial'],
          ['Pricing', 'Free Access', 'Free', 'Often Paid'],
          ['Beginner Friendly', 'Yes', 'Yes', 'Partial'],
          ['Link Verification', 'Fully Verified', 'Not Guaranteed', 'Mixed'],
        ],
      },
      faqs: [
        { question: 'Is OOPBuy Spreadsheet safe to use?', answer: 'Yes. The OOPBuy Spreadsheet is an informational resource that links to verified product pages. All external links are clearly marked and open in new tabs. We recommend exercising standard online shopping precautions when making any purchase.' },
        { question: 'Do I need an account to use OOPBuy Spreadsheet?', answer: 'No account is required to browse the spreadsheet or use category navigation. However, purchasing through Kaspreadsheet may require creating an account on their platform.' },
        { question: 'How often is the OOPBuy Spreadsheet updated?', answer: 'The spreadsheet and linked product pages are updated regularly to reflect current inventory and pricing. Category links are verified periodically to ensure they remain active.' },
        { question: 'Can I suggest new categories or products?', answer: 'The 14 current categories cover the main product types available through Kaspreadsheet. For suggestions, we recommend reaching out through the main platforms contact channels.' },
      ],
    },
    '/oopbuy-spreadsheet-review': {
      metaTitle: 'OOPBuy Spreadsheet Review 2026 – Honest Analysis & User Experience',
      metaDescription: 'In-depth OOPBuy Spreadsheet review covering real user experience, pricing, features, pros and cons. Honest analysis of whether OOPBuy Spreadsheet is worth using in 2026.',
      sections: [
        {
          heading: 'First Impressions: Getting Started with OOPBuy Spreadsheet',
          paragraphs: [
            'From the moment you first access the OOPBuy Spreadsheet, the experience feels streamlined and professional. The category organization is logical, with 14 clearly labeled sections spanning everything from Shoes to Perfumes. Unlike cluttered marketplaces that overwhelm users with options, the OOPBuy Spreadsheet presents data in a clean, scannable format.',
            'Loading times are consistently fast, and the category links respond instantly. The Kaspreadsheet platform integration is seamless — clicking any category link takes you directly to relevant, verified product pages without redirect chains or dead ends.',
            '<strong>Navigation is refreshingly simple</strong>. The category dropdown and visual grid make finding specific product types easy, even for first-time visitors. This accessibility is one of the strongest selling points of the OOPBuy Spreadsheet ecosystem.',
          ],
        },
        {
          heading: 'Deep Dive: Features and Performance',
          paragraphs: [
            'The standout feature of OOPBuy Spreadsheet is its <strong>category navigation system</strong>. Rather than forcing users to memorize URLs or bookmark dozens of pages, the spreadsheet centralizes access to all 14 product categories. Each link is verified, ensuring you never waste time on broken pages.',
            'The <strong>comparison capabilities</strong> are another strong point. Users can quickly scan product details, pricing, and availability across multiple categories without opening separate tabs. For buyers managing bulk orders, this alone can save hours per week.',
            'On the reliability front, the OOPBuy Spreadsheet excels. Unlike aggregators that scrape outdated listings, this platform maintains verified connections to active product pages. Regular updates ensure the data remains current and trustworthy.',
          ],
        },
        {
          heading: 'Pros and Cons: An Honest Breakdown',
          paragraphs: [
            '<strong>Pros:</strong> The OOPBuy Spreadsheet excels at simplicity, reliability, and cost-effectiveness. Its free access model makes it accessible to anyone, and the verified link system provides peace of mind that less organized sourcing methods cannot match.',
            '<strong>Cons:</strong> The spreadsheet depends entirely on the Kaspreadsheet ecosystem, meaning you are limited to the products and categories available on that platform. Users seeking products outside these 14 categories will need supplementary tools.',
            'Overall, for buyers whose needs align with the covered categories, the pros heavily outweigh the cons. The time savings and organizational benefits alone make OOPBuy Spreadsheet a valuable addition to any sourcing toolkit.',
          ],
        },
      ],
      table: {
        headers: ['Criteria', 'Rating', 'Notes'],
        rows: [
          ['Ease of Use', 'Excellent', 'Intuitive category navigation'],
          ['Reliability', 'Excellent', 'All links verified regularly'],
          ['Feature Set', 'Very Good', 'Covers 14 categories thoroughly'],
          ['Pricing Value', 'Excellent', 'Free core access'],
          ['Beginner Friendly', 'Excellent', 'No technical skills needed'],
          ['Platform Independence', 'Good', 'Tied to Kaspreadsheet only'],
        ],
      },
      faqs: [
        { question: 'Is OOPBuy Spreadsheet worth it in 2026?', answer: 'For buyers actively sourcing across the 14 fashion categories covered, OOPBuy Spreadsheet offers excellent value — especially given its free access model. The verified links and organized structure save significant time compared to manual browsing.' },
        { question: 'Are there any hidden fees?', answer: 'The core OOPBuy Spreadsheet features are free to access. Purchasing products through Kaspreadsheet may involve standard retail costs, but the spreadsheet itself does not charge subscription fees.' },
        { question: 'How does it compare to other sourcing tools?', answer: 'Compared to other product sourcing aggregators, OOPBuy Spreadsheet stands out for its simplicity and link verification. While some competitors offer broader category coverage, few match its reliability-to-cost ratio.' },
      ],
    },
    '/oopbuy-spreadsheet-comparison': {
      metaTitle: 'OOPBuy Spreadsheet vs Alternatives – Complete Comparison Guide 2026',
      metaDescription: 'Side-by-side comparison of OOPBuy Spreadsheet vs other product sourcing tools. Analyze features, pricing, ease of use, and reliability to choose the best option for your needs.',
      sections: [
        {
          heading: 'OOPBuy Spreadsheet vs Traditional Sourcing Methods',
          paragraphs: [
            'Traditional product sourcing typically involves manually browsing supplier websites, maintaining personal spreadsheets, and verifying product availability across multiple platforms. This approach works but is time-consuming and prone to errors — links go dead, prices change without notice, and organization becomes chaotic as your catalog grows.',
            'The OOPBuy Spreadsheet eliminates these pain points by centralizing verified links into a structured, regularly updated document. Instead of spending hours maintaining your own records, you access a pre-built, professionally maintained catalog. For serious buyers, this time savings alone justifies adopting the spreadsheet.',
          ],
        },
        {
          heading: 'Feature-by-Feature Comparison',
          paragraphs: [
            'When comparing <strong>category coverage</strong>, the OOPBuy Spreadsheet offers 14 curated fashion and lifestyle categories. While some aggregators cover more categories, the spreadsheet prioritizes quality over quantity — each link is verified, and each category is populated with active, relevant products.',
            'On <strong>pricing</strong>, OOPBuy Spreadsheet holds a clear advantage. Its free access model contrasts sharply with many alternatives that charge monthly subscriptions. Even premium-sounding competitors often lock essential features behind paywalls, making the spreadsheet a more accessible entry point for new buyers.',
          ],
        },
        {
          heading: 'Which Tool Is Right for You?',
          paragraphs: [
            'If you prioritize <strong>simplicity, reliability, and cost-effectiveness</strong>, OOPBuy Spreadsheet is the clear winner. Its verified links, intuitive navigation, and free access make it ideal for individual buyers, resellers, and small-scale sourcing agents.',
            'If you need <strong>broader category coverage</strong> beyond the 14 categories or require API integrations for automated workflows, you may want to supplement the spreadsheet with additional tools. However, for most fashion-focused buyers, the OOPBuy Spreadsheet provides everything needed in one clean package.',
          ],
        },
      ],
      table: {
        headers: ['Feature', 'OOPBuy Spreadsheet', 'Manual Browsing', 'Paid Aggregators'],
        rows: [
          ['Categories', '14 verified', 'Unlimited but unorganized', 'Varies widely'],
          ['Link Verification', 'Fully verified', 'Not guaranteed', 'Often mixed'],
          ['Pricing', 'Free', 'Free', 'Usually $10-$50/month'],
          ['Learning Curve', 'Minimal', 'Minimal', 'Often steep'],
          ['Updates', 'Regular', 'Manual only', 'Varies'],
          ['Best For', 'Fashion buyers', 'Casual browsing', 'Enterprise sourcing'],
        ],
      },
      faqs: [
        { question: 'Can OOPBuy Spreadsheet replace all other sourcing tools?', answer: 'For buyers focused on the 14 fashion categories covered, OOPBuy Spreadsheet can serve as a primary sourcing tool. Those needing broader coverage may use it alongside other resources.' },
        { question: 'Is OOPBuy Spreadsheet better than paid alternatives?', answer: 'For its specific use case — organized fashion product sourcing — OOPBuy Spreadsheet often outperforms paid alternatives in simplicity and reliability, despite being free.' },
        { question: 'How often should I cross-reference with other tools?', answer: 'Since OOPBuy Spreadsheet links are regularly verified, daily cross-referencing is unnecessary. Weekly spot-checks are sufficient for most users.' },
      ],
    },
  };

  if (contentMap[cleanSlug]) return contentMap[cleanSlug];

  const article = getArticleBySlug(cleanSlug);
  return {
    metaTitle: `${article.title} – OOPBuy Spreadsheet Resources 2026`,
    metaDescription: article.description.substring(0, 155),
    sections: [
      {
        heading: `Understanding ${article.title}`,
        paragraphs: [
          `The <strong>OOPBuy Spreadsheet</strong> ecosystem has grown significantly, and understanding ${article.title.toLowerCase()} is essential for any serious product sourcer. Whether you are a first-time buyer or an experienced sourcing agent, the insights on this page will help you navigate the platform more effectively.`,
          `At OOPBuy Spreadsheet, we prioritize <strong>verified information and actionable guidance</strong>. Every article in our resource library is written to help you save time, avoid common pitfalls, and discover better deals across all 14 product categories — from Shoes and Hoodies to Accessories and Perfumes.`,
          'The OOPBuy Spreadsheet approach is refreshingly straightforward. Rather than overwhelming users with complex dashboards or steep learning curves, it focuses on what matters most: organized, verified access to the products you need.',
        ],
      },
      {
        heading: 'Key Insights for OOPBuy Spreadsheet Users',
        paragraphs: [
          `When diving into <strong>${article.title.toLowerCase()}</strong>, the most important factor is understanding how the spreadsheet organizes and verifies product information. The 14-category structure is designed to make navigation intuitive, and all external links are regularly checked to ensure they remain active and relevant.`,
          'Another crucial aspect is <strong>consistency</strong>. The spreadsheet is updated on a regular basis, meaning the product data you access today reflects current availability. This dynamic approach sets OOPBuy Spreadsheet apart from static catalogs that quickly become outdated.',
          'For buyers who source products regularly, the time savings compound quickly. What might take hours of manual searching elsewhere can often be accomplished in minutes using the organized category structure of OOPBuy Spreadsheet.',
        ],
      },
      {
        heading: 'Practical Next Steps',
        paragraphs: [
          'If you are ready to put this knowledge into practice, the best next step is to explore the category links available on the homepage. Each of the 14 categories connects directly to verified product pages on Kaspreadsheet, giving you immediate access to curated, active listings.',
          'For buyers who want to stay updated, we recommend bookmarking the OOPBuy Spreadsheet homepage and checking back regularly. New resources, updated guides, and the latest category links are always available.',
          'Ready to start sourcing? Visit Kaspreadsheet now and experience the streamlined product discovery that thousands of buyers already trust for their daily sourcing needs.',
        ],
      },
    ],
    table: {
      headers: ['Aspect', 'OOPBuy Spreadsheet', 'Traditional Methods'],
      rows: [
        ['Organization', '14 curated categories', 'Scattered across sources'],
        ['Verification', 'All links verified', 'Self-verified only'],
        ['Updates', 'Regular automatic updates', 'Manual tracking required'],
        ['Cost', 'Free access', 'Time and effort costs'],
        ['Learning Curve', 'Beginner friendly', 'Varies by experience'],
      ],
    },
    faqs: [
      { question: 'How do I get started with OOPBuy Spreadsheet?', answer: 'The best way to start is by visiting the OOPBuy Spreadsheet homepage and exploring the category links. Each link takes you directly to verified product pages on Kaspreadsheet. No account or technical setup is required.' },
      { question: 'Is the information on this page regularly updated?', answer: 'Yes. All OOPBuy Spreadsheet resources are periodically reviewed and updated to ensure accuracy and relevance for current buyers in 2026.' },
      { question: 'Can I access OOPBuy Spreadsheet on mobile?', answer: 'Yes, the OOPBuy Spreadsheet resource site is fully responsive and works on mobile devices, tablets, and desktop computers without any loss of functionality.' },
    ],
  };
}

function renderParagraph(text: string, key: number) {
  const parts = text.split(/(<strong>.*?<\/strong>)/g);
  return (
    <p key={key} className="text-foreground-700 text-base leading-relaxed mb-4">
      {parts.map((part, i) => {
        if (part.startsWith('<strong>') && part.endsWith('</strong>')) {
          return <strong key={i}>{part.replace(/<\/?strong>/g, '')}</strong>;
        }
        return part;
      })}
    </p>
  );
}

function buildArticleJsonLd(articleData: { title: string; description: string; category: string }, content: ArticleContent, slug: string) {
  const currentUrl = `${SITE_URL}${slug}`;

  const articleSchema: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: content.metaTitle,
    description: content.metaDescription,
    url: currentUrl,
    datePublished: '2026-06-15',
    dateModified: '2026-06-21',
    author: {
      '@type': 'Organization',
      name: 'OOPBuy Spreadsheet',
      url: SITE_URL,
    },
    publisher: {
      '@type': 'Organization',
      name: 'OOPBuy Spreadsheet',
      url: SITE_URL,
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': currentUrl,
    },
    inLanguage: 'en',
    articleSection: articleData.category,
  };

  return articleSchema;
}

function buildBreadcrumbJsonLd(slug: string, title: string) {
  const currentUrl = `${SITE_URL}${slug}`;
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: SITE_URL,
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: title,
        item: currentUrl,
      },
    ],
  };
}

function buildFaqJsonLd(faqs: { question: string; answer: string }[], slug: string) {
  const currentUrl = `${SITE_URL}${slug}`;
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': currentUrl,
    },
  };
}

export default function ArticlePage() {
  const location = useLocation();
  const slug = location.pathname;
  const article = getArticleBySlug(slug);
  const content = getArticleContent(slug);

  const articleJsonLd = buildArticleJsonLd(article, content, slug);
  const breadcrumbJsonLd = buildBreadcrumbJsonLd(slug, article.title);
  const faqJsonLd = content.faqs.length > 0 ? buildFaqJsonLd(content.faqs, slug) : null;

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
    document.title = content.metaTitle;
    document.querySelector('meta[name="description"]')?.setAttribute('content', content.metaDescription);
  }, [slug, content.metaTitle, content.metaDescription]);

  return (
    <div className="min-h-screen bg-background-50">
      <JsonLd data={articleJsonLd} />
      <JsonLd data={breadcrumbJsonLd} />
      {faqJsonLd && <JsonLd data={faqJsonLd} />}
      <Header />
      <main className="pt-28 pb-16">
        <article className="max-w-[780px] mx-auto px-6 md:px-10">
          <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-8" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-primary-500 transition-colors duration-150 cursor-pointer">
              Home
            </Link>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <span className="text-foreground-700 font-medium">{article.title}</span>
          </nav>

          <header className="mb-10">
            <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl font-extrabold text-foreground-900 leading-tight mb-4">
              {article.title}
            </h1>
            <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500">
              <span>Updated: June 2026</span>
              <span className="w-1 h-1 rounded-full bg-foreground-400"></span>
              <span>By OOPBuy Spreadsheet Team</span>
              <span className="w-1 h-1 rounded-full bg-foreground-400"></span>
              <span className="text-accent-600 font-medium">{article.category}</span>
            </div>
          </header>

          <div className="prose-custom">
            {content.sections.map((section, sIdx) => (
              <div key={sIdx}>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mt-12 mb-5">
                  {section.heading}
                </h2>
                {section.paragraphs.map((p, pIdx) => renderParagraph(p, pIdx))}
              </div>
            ))}

            {content.table && (
              <div className="overflow-x-auto mb-8 rounded-xl border border-background-200 mt-10">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-background-100">
                      {content.table.headers.map((h, i) => (
                        <th key={i} className={`text-left px-5 py-3.5 font-semibold ${i === 1 ? 'text-primary-600' : 'text-foreground-900'}`}>
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-background-200">
                    {content.table.rows.map((row, rIdx) => (
                      <tr key={rIdx} className={rIdx % 2 === 0 ? 'bg-white' : 'bg-background-50'}>
                        {row.map((cell, cIdx) => (
                          <td key={cIdx} className={`px-5 py-3.5 ${cIdx === 0 ? 'font-medium text-foreground-800' : cIdx === 1 ? 'text-accent-600 font-semibold' : 'text-foreground-600'}`}>
                            {cell}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {content.faqs.length > 0 && (
              <>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mt-12 mb-5">
                  Frequently Asked Questions
                </h2>
                <div className="space-y-4 mb-10">
                  {content.faqs.map((faq, fIdx) => (
                    <div key={fIdx} className="bg-white rounded-xl border border-background-200 p-5">
                      <h4 className="font-semibold text-foreground-900 mb-2">
                        {faq.question}
                      </h4>
                      <p className="text-sm text-foreground-600 leading-relaxed">
                        {faq.answer}
                      </p>
                    </div>
                  ))}
                </div>
              </>
            )}

            <div className="border-t border-background-200 pt-10 mt-10">
              <h3 className="font-heading text-xl font-bold text-foreground-900 mb-5">
                More OOPBuy Resources
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <Link
                  to="/"
                  className="flex items-center gap-3 p-4 bg-white rounded-xl border border-background-200 hover:border-primary-200 transition-all duration-200 cursor-pointer"
                >
                  <span className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 flex-shrink-0">
                    <i className="ri-home-4-line text-lg"></i>
                  </span>
                  <div>
                    <span className="text-sm font-semibold text-foreground-900 block">Home</span>
                    <span className="text-xs text-foreground-500">Main guide hub</span>
                  </div>
                </Link>
                <Link
                  to="/what-is-oopbuy-spreadsheet"
                  className="flex items-center gap-3 p-4 bg-white rounded-xl border border-background-200 hover:border-primary-200 transition-all duration-200 cursor-pointer"
                >
                  <span className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 flex-shrink-0">
                    <i className="ri-book-read-line text-lg"></i>
                  </span>
                  <div>
                    <span className="text-sm font-semibold text-foreground-900 block">Guide</span>
                    <span className="text-xs text-foreground-500">Beginner walkthrough</span>
                  </div>
                </Link>
                <a
                  href="https://kaspreadsheet.com/"
                  target="_blank"
                  rel="noopener nofollow"
                  className="flex items-center gap-3 p-4 bg-white rounded-xl border border-background-200 hover:border-primary-200 transition-all duration-200 cursor-pointer"
                >
                  <span className="w-10 h-10 flex items-center justify-center rounded-lg bg-foreground-100 text-foreground-700 flex-shrink-0">
                    <i className="ri-external-link-line text-lg"></i>
                  </span>
                  <div>
                    <span className="text-sm font-semibold text-foreground-900 block">Kaspreadsheet</span>
                    <span className="text-xs text-foreground-500">Main platform</span>
                  </div>
                </a>
              </div>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row items-center gap-4 mt-12 pt-8 border-t border-background-200">
            <Link
              to="/"
              className="w-full sm:w-auto px-6 py-3 rounded-full bg-foreground-900 text-white text-sm font-semibold hover:bg-foreground-800 transition-colors duration-200 text-center whitespace-nowrap cursor-pointer"
            >
              Back to Home
            </Link>
            <a
              href="https://kaspreadsheet.com/"
              target="_blank"
              rel="noopener nofollow"
              className="w-full sm:w-auto px-6 py-3 rounded-full border-2 border-foreground-800 text-foreground-800 text-sm font-semibold hover:bg-foreground-800 hover:text-white transition-colors duration-200 text-center whitespace-nowrap cursor-pointer"
            >
              Visit Kaspreadsheet
            </a>
          </div>
        </article>
      </main>
      <Footer />
    </div>
  );
}